import React, { useMemo, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { ViewPeriod } from "./ContributionHeatmap.service";
import ContributionHeatMapWeek from "../ContributionHeatMapWeek";

interface HeatmapData {
  date: string;
  count: number;
}

interface ContributionHeatmapProps {
  data: HeatmapData[];
  viewPeriod: ViewPeriod;
  onCellClick?: (date: string) => void;
  cellSize?: number;
  gap?: number;
}

const HeatmapContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  padding: "16px",
  backgroundColor: theme.palette.background.paper,
  borderRadius: "8px",
  width: "100%",
}));

const ContributionHeatmap: React.FC<ContributionHeatmapProps> = ({
  data,
  onCellClick,
  cellSize = 20,
  gap = 2,
}) => {
  // Référence pour le conteneur de scroll horizontal
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Generate data for a full year (52 weeks)
  const weeksData = useMemo(() => {
    const today = new Date();
    const WEEKS_IN_YEAR = 52;

    // Create a map for quick lookup of habit entries by date
    const habitEntriesMap = new Map<string, number>();

    // Helper function to generate week data
    const generateWeekData = (startDate: Date) => {
      const weekData = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);

        const dateString = date.toISOString().split("T")[0];

        // Use real habit entries count, or fallback to provided data, or 0
        const habitEntriesCount = habitEntriesMap.get(dateString) || 0;
        const providedDataCount =
          data.find((d) => d.date === dateString)?.count || 0;
        const count =
          habitEntriesCount > 0 ? habitEntriesCount : providedDataCount;

        weekData.push({
          date: dateString,
          count,
        });
      }
      return weekData;
    };

    // Start from Monday of current week
    const currentMonday = new Date(today);
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    currentMonday.setDate(today.getDate() - daysToSubtract);

    // Generate 52 weeks of data (current week + 51 previous weeks)
    const weeks = [];
    for (let weekOffset = WEEKS_IN_YEAR - 1; weekOffset >= 0; weekOffset--) {
      const weekStartDate = new Date(currentMonday);
      weekStartDate.setDate(currentMonday.getDate() - weekOffset * 7);
      weeks.push(generateWeekData(weekStartDate));
    }

    return weeks;
  }, [data]);

  // Effet pour faire défiler automatiquement vers la droite (jours les plus récents)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      // Scroll vers la droite après que le composant soit monté et que les données soient chargées
      const scrollToRight = () => {
        scrollContainer.scrollLeft =
          scrollContainer.scrollWidth - scrollContainer.clientWidth;
      };

      // Petit délai pour s'assurer que le rendu est terminé
      const timer = setTimeout(scrollToRight, 100);

      return () => clearTimeout(timer);
    }
  }, [weeksData]); // Se déclenche quand weeksData change

  return (
    <HeatmapContainer>
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          gap: "2px",
          width: "100%",
          overflowX: "auto",
          paddingBottom: "8px",
        }}
      >
        {weeksData.map((weekData, index) => (
          <ContributionHeatMapWeek
            key={index}
            weekData={weekData}
            cellSize={cellSize}
            gap={gap}
            onCellClick={onCellClick}
          />
        ))}
      </Box>
    </HeatmapContainer>
  );
};

export default ContributionHeatmap;
